import { useMemo, useState } from "react";
import {
  DndContext,
  type DragEndEvent,
  type DragStartEvent,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  closestCorners,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import { toast } from "sonner";
import { completeTask, reopenTask, updateTask } from "@/api/tasks";
import { PRIORITY_RANK } from "./priority";
import { paletteFor } from "./statusPalette";
import {
  TaskKanbanColumn,
  type KanbanColumnConfig,
} from "./TaskKanbanColumn";
import { TaskKanbanCard } from "./TaskKanbanCard";
import type { TaskRead, TaskStatusConfig } from "@/api/types";

/** Legacy two-column fallback used when the project hasn't configured
 *  task statuses (or the backend hasn't shipped the endpoint yet). */
const FALLBACK_COLUMNS: KanbanColumnConfig[] = [
  {
    id: "todo",
    label: "To do",
    apply: { kind: "boolean", isCompleted: false },
    ...paletteFor("slate"),
  },
  {
    id: "done",
    label: "Done",
    apply: { kind: "boolean", isCompleted: true },
    ...paletteFor("emerald"),
  },
];

interface TaskKanbanBoardProps {
  tasks: TaskRead[];
  projectSlug: string;
  canEdit: boolean;
  canDelete: boolean;
  loading?: boolean;
  /** Project-configured task statuses (from GET /tasks/statuses). When
   *  non-empty, drives one column per status in `order`. Empty / null
   *  → falls back to the legacy two-column is_completed behavior. */
  statuses?: TaskStatusConfig[];
  /** When set, only columns whose status key is in the array are
   *  rendered. Driven by the page's status multi-select filter. The
   *  empty array (default) means "no filter — show every column". */
  visibleStatusKeys?: ReadonlyArray<string>;
  /** When `true`, the soft 10-card cap on done columns is removed —
   *  used when the page's "Completed" quick filter is active so the
   *  user sees every completed task without an extra click. */
  expandDoneColumns?: boolean;
  onTaskUpdated: (task: TaskRead) => void;
  onOpenDetail: (task: TaskRead) => void;
  onAskDelete?: (task: TaskRead) => void;
  /** Open the Create dialog. The column carries either a status key
   *  (dynamic) or an isCompleted boolean (legacy) so the dialog can
   *  pre-fill correctly. */
  onAddTask: (column: KanbanColumnConfig) => void;
}

export function TaskKanbanBoard({
  tasks,
  projectSlug,
  canEdit,
  canDelete,
  loading = false,
  statuses,
  visibleStatusKeys,
  expandDoneColumns = false,
  onTaskUpdated,
  onOpenDetail,
  onAskDelete,
  onAddTask,
}: TaskKanbanBoardProps) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const [activeTask, setActiveTask] = useState<TaskRead | null>(null);

  // Resolve the column set. Dynamic when the project configured
  // statuses; the legacy two-column shape otherwise.
  const allColumns = useMemo<KanbanColumnConfig[]>(() => {
    if (!statuses || statuses.length === 0) return FALLBACK_COLUMNS;
    return statuses
      .slice()
      .sort((a, b) => a.order - b.order)
      .map((s) => ({
        id: `status:${s.key}`,
        label: s.label,
        apply: {
          kind: "status",
          statusKey: s.key,
          isDone: !!s.is_done,
        },
        ...paletteFor(s.color),
      }));
  }, [statuses]);

  // Apply the page-level status filter. An empty / undefined array
  // means "no filter" — show everything.
  const columns = useMemo<KanbanColumnConfig[]>(() => {
    if (!visibleStatusKeys || visibleStatusKeys.length === 0) {
      return allColumns;
    }
    const allow = new Set(visibleStatusKeys);
    return allColumns.filter((c) => {
      if (c.apply.kind === "status") return allow.has(c.apply.statusKey);
      // Legacy fallback columns don't carry a status key, so the
      // status filter doesn't apply to them — keep them visible.
      return true;
    });
  }, [allColumns, visibleStatusKeys]);

  // Collapsed columns. Every column starts expanded — done cards stay
  // visible by default so a freshly-completed task doesn't vanish from
  // the board. The user can manually collapse any done column via the
  // chevron on its header.
  const [collapsed, setCollapsed] = useState<Set<string>>(() => new Set());

  function toggleCollapsed(col: KanbanColumnConfig) {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(col.id)) next.delete(col.id);
      else next.add(col.id);
      return next;
    });
  }

  // Bucket tasks per column. With dynamic statuses we route by
  // task.status; tasks missing a status are bucketed into the initial
  // status (or the first column if no initial is flagged) so legacy
  // tasks created before status-config still show up. With the
  // fallback, route by is_completed.
  const tasksByColumn = useMemo<Record<string, TaskRead[]>>(() => {
    const out: Record<string, TaskRead[]> = {};
    for (const c of columns) out[c.id] = [];
    const useDynamic = !!statuses && statuses.length > 0;
    const dynamicFallbackColumnId =
      columns.find((c) => {
        if (c.apply.kind !== "status") return false;
        const statusKey = c.apply.statusKey;
        return !!statuses?.find((s) => s.key === statusKey)?.is_initial;
      })?.id ?? columns[0]?.id;
    for (const t of tasks) {
      let columnId: string | null = null;
      if (useDynamic) {
        if (t.status) {
          const found = columns.find(
            (c) =>
              c.apply.kind === "status" && c.apply.statusKey === t.status,
          );
          if (found) columnId = found.id;
        }
        if (!columnId) columnId = dynamicFallbackColumnId ?? null;
      } else {
        columnId = t.is_completed ? "done" : "todo";
      }
      if (columnId && out[columnId]) out[columnId].push(t);
    }
    for (const id of Object.keys(out)) out[id].sort(compareForColumn);
    return out;
  }, [tasks, columns, statuses]);

  function findColumnIdForTask(taskId: number): string | null {
    for (const col of columns) {
      if (tasksByColumn[col.id]?.some((t) => t.id === taskId)) {
        return col.id;
      }
    }
    return null;
  }

  function handleDragStart(event: DragStartEvent) {
    const id = event.active.id;
    const t = tasks.find((x) => x.id === id);
    if (t) setActiveTask(t);
  }

  async function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    setActiveTask(null);
    if (!over) return;
    const taskId = Number(active.id);
    const task = tasks.find((t) => t.id === taskId);
    if (!task) return;
    // Cross-project rows (the My Tasks feed) carry their own project
    // slug — mutate against *that* project, not the board's. Per-project
    // rows have no project_slug and fall back to the board slug.
    const taskSlug =
      (task as { project_slug?: string }).project_slug ?? projectSlug;

    let targetColumnId: string | null = null;
    if (
      typeof over.id === "string" &&
      columns.some((c) => c.id === over.id)
    ) {
      targetColumnId = over.id;
    } else {
      targetColumnId = findColumnIdForTask(Number(over.id));
    }
    if (!targetColumnId) return;
    const targetCol = columns.find((c) => c.id === targetColumnId);
    if (!targetCol) return;

    // Optimistic update + which API call to fire depends on the
    // column's apply mechanism.
    if (targetCol.apply.kind === "status") {
      if (task.status === targetCol.apply.statusKey) return;
      const optimistic: TaskRead = {
        ...task,
        status: targetCol.apply.statusKey,
        is_completed: targetCol.apply.isDone,
        completed_at: targetCol.apply.isDone
          ? new Date().toISOString()
          : null,
      };
      onTaskUpdated(optimistic);
      try {
        const updated = await updateTask(taskSlug, taskId, {
          status: targetCol.apply.statusKey,
        });
        onTaskUpdated(updated);
        toast.success(`Task moved to ${targetCol.label}`);
      } catch (err) {
        onTaskUpdated(task);
        toast.error(extractMessage(err) || "Couldn't move the task.");
      }
    } else {
      // Legacy boolean fallback.
      if (task.is_completed === targetCol.apply.isCompleted) return;
      const optimistic: TaskRead = {
        ...task,
        is_completed: targetCol.apply.isCompleted,
        completed_at: targetCol.apply.isCompleted
          ? new Date().toISOString()
          : null,
      };
      onTaskUpdated(optimistic);
      try {
        const updated = targetCol.apply.isCompleted
          ? await completeTask(taskSlug, taskId)
          : await reopenTask(taskSlug, taskId);
        onTaskUpdated(updated);
        toast.success(`Task moved to ${targetCol.label}`);
      } catch (err) {
        onTaskUpdated(task);
        toast.error(extractMessage(err) || "Couldn't move the task.");
      }
    }
  }

  if (columns.length === 0) {
    // Status filter excluded everything — show a hint instead of an
    // empty grid.
    return (
      <div className="rounded-plane bg-white px-6 py-16 text-center shadow-plane">
        <p className="text-sm text-slate-500">
          No columns match the current status filter.
        </p>
      </div>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={() => setActiveTask(null)}
    >
      <div
        className="grid gap-4"
        style={{
          gridTemplateColumns: `repeat(${columns.length}, minmax(0, 1fr))`,
        }}
      >
        {columns.map((col) => {
          const isDone =
            (col.apply.kind === "status" && col.apply.isDone) ||
            (col.apply.kind === "boolean" && col.apply.isCompleted);
          return (
            <TaskKanbanColumn
              key={col.id}
              column={col}
              tasks={tasksByColumn[col.id] ?? []}
              projectSlug={projectSlug}
              canEdit={canEdit}
              canDelete={canDelete}
              loading={loading}
              collapsed={collapsed.has(col.id)}
              // Only Done columns expose the chevron — the rest stay
              // open at all times. Done columns are expanded by
              // default; the chevron is the manual hide-mine
              // affordance.
              onToggleCollapsed={isDone ? toggleCollapsed : undefined}
              // Cards in done columns are dimmed and struck through so
              // they read as "archived" without disappearing. The
              // 10-card soft cap keeps the column from dominating the
              // viewport; expandDoneColumns lifts it for the
              // Completed quick filter.
              dimCards={isDone}
              cap={isDone && !expandDoneColumns ? 10 : undefined}
              // "+" appears on every non-done column. The Add dialog is
              // pre-filled with the column's status key (dynamic) or a
              // sensible default (legacy).
              showAddButton={!isDone}
              onAddTask={onAddTask}
              onOpenDetail={onOpenDetail}
              onAskDelete={onAskDelete}
            />
          );
        })}
      </div>

      <DragOverlay>
        {activeTask && (
          <TaskKanbanCard
            task={activeTask}
            projectSlug={projectSlug}
            canDelete={false}
            isOverlay
            onOpenDetail={() => undefined}
          />
        )}
      </DragOverlay>
    </DndContext>
  );
}

function compareForColumn(a: TaskRead, b: TaskRead): number {
  const dp =
    (PRIORITY_RANK[b.priority] ?? 0) - (PRIORITY_RANK[a.priority] ?? 0);
  if (dp !== 0) return dp;
  const ad = a.due_at ? Date.parse(a.due_at) : Number.POSITIVE_INFINITY;
  const bd = b.due_at ? Date.parse(b.due_at) : Number.POSITIVE_INFINITY;
  if (ad !== bd) return ad - bd;
  return a.id - b.id;
}

function extractMessage(err: unknown): string | null {
  if (err && typeof err === "object" && "message" in err) {
    return String((err as { message?: unknown }).message ?? "");
  }
  return null;
}
